import { IParameterDef, ParameterLevel, ParameterOption } from 'interfaces/IFleetMonitoring'
import strings from 'strings'

const FM = strings.FLEET_MONITORING

// A select option may be a bare string (value === label) or a { value, label } pair.
export const optionValue = (option: ParameterOption): string =>
  typeof option === 'string' ? option : option.value

export const optionLabel = (option: ParameterOption): string =>
  typeof option === 'string' ? option : option.label

// A param is in scope for a given level when it applies to that level (or to both / no level set).
export const levelAllows = (
  applicable: ParameterLevel | null | undefined,
  level?: ParameterLevel,
): boolean => !level || !applicable || applicable === 'both' || applicable === level

interface IValidateOptions {
  level?: ParameterLevel
  // Resolved computed defaults (e.g. policy count) keyed by param_key — when present they satisfy
  // a default_source param; when absent the param effectively requires a manual value.
  inheritedValues?: Record<string, unknown>
}

/**
 * Validates parameter values against their defs (data type + rules). Tolerates string or typed
 * values (works on the local draft and on persisted values). Returns a map paramKey → error message
 * (empty = all valid). Shared by the input component and the parent save flows so the rules live in
 * one place.
 */
export const validateParameterValues = (
  defs: IParameterDef[],
  values: Record<string, unknown> | undefined,
  options: IValidateOptions = {},
): Record<string, string> => {
  const errors: Record<string, string> = {}
  const scoped = (defs || []).filter(def => levelAllows(def.applicableLevels, options.level))
  scoped.forEach(def => {
    if (def.unitType === 'boolean') return // a boolean always carries a value
    const raw = values ? values[def.paramKey] : undefined
    const str = raw === undefined || raw === null ? '' : String(raw).trim()
    const rules = def.rules || {}
    const hasInherited = !!(options.inheritedValues && options.inheritedValues[def.paramKey] != null)

    if (str === '') {
      if (def.required && !(def.defaultValue ?? '')) {
        errors[def.paramKey] = FM.COMMON.PARAM_REQUIRED_ERROR
      } else if (def.defaultSource && !hasInherited && !(def.defaultValue ?? '')) {
        // Auto-defaulted param (e.g. expected count ← policy) with no resolved default available
        // → the user must enter a value, otherwise the validation can't evaluate.
        errors[def.paramKey] = FM.COMMON.PARAM_NEEDS_VALUE
      }
      return
    }

    if (def.unitType === 'integer' || def.unitType === 'decimal') {
      const n = Number(str)
      if (Number.isNaN(n)) {
        errors[def.paramKey] = FM.COMMON.PARAM_NUMBER_ERROR
        return
      }
      if (def.unitType === 'integer' && !Number.isInteger(n)) {
        errors[def.paramKey] = FM.COMMON.PARAM_INTEGER_ERROR
        return
      }
      const min = rules.min != null ? Number(rules.min) : null
      const max = rules.max != null ? Number(rules.max) : null
      if (min != null && !Number.isNaN(min) && n < min) {
        errors[def.paramKey] = FM.COMMON.PARAM_MIN_ERROR(min)
        return
      }
      if (max != null && !Number.isNaN(max) && n > max) {
        errors[def.paramKey] = FM.COMMON.PARAM_MAX_ERROR(max)
      }
      return
    }

    if (def.unitType === 'date') {
      // ISO yyyy-MM-dd compares lexicographically.
      const min = rules.min != null ? String(rules.min) : null
      const max = rules.max != null ? String(rules.max) : null
      if (min && str < min) {
        errors[def.paramKey] = FM.COMMON.PARAM_MIN_DATE_ERROR(min)
        return
      }
      if (max && str > max) {
        errors[def.paramKey] = FM.COMMON.PARAM_MAX_DATE_ERROR(max)
      }
      return
    }

    if (def.unitType === 'select') {
      const allowed = (rules.options || []).map(optionValue)
      if (allowed.length > 0 && !allowed.includes(str)) {
        errors[def.paramKey] = FM.COMMON.PARAM_OPTION_ERROR
      }
      return
    }

    if (def.unitType === 'text') {
      if (rules.minLength != null && str.length < rules.minLength) {
        errors[def.paramKey] = FM.COMMON.PARAM_MINLEN_ERROR(rules.minLength)
        return
      }
      if (rules.maxLength != null && str.length > rules.maxLength) {
        errors[def.paramKey] = FM.COMMON.PARAM_MAXLEN_ERROR(rules.maxLength)
      }
    }
  })
  return errors
}

export const hasParameterErrors = (
  defs: IParameterDef[],
  values: Record<string, unknown> | undefined,
  options: IValidateOptions = {},
): boolean => Object.keys(validateParameterValues(defs, values, options)).length > 0
